import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const filters = ['All', 'Web', 'Mobile', 'UI/UX', 'Cloud'];

  // Load projects from the generated index (public/portfolio-index.json)
  const [projects, setProjects] = useState<any[]>([]);

  React.useEffect(() => {
    fetch('/portfolio-index.json')
      .then(r => r.json())
      .then((data) => {
        if (data && Array.isArray(data.projects)) {
          setProjects(data.projects.map((p: any) => ({
            title: p.title,
            category: p.category,
            desc: p.desc,
            image: p.thumbnail,
            files: p.files || [],
            icon: Globe
          })));
        }
      }).catch(() => {
        // fallback: empty array
        setProjects([]);
      });
  }, []);

  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  const openLightbox = (project: any) => {
    // Use project's files array from the index if available. Otherwise fallback to image.
    const files = project.files && project.files.length ? project.files : (project.image ? [project.image] : []);
    // Validate by attempting to load images/videos; for videos we can't easily probe without fetching, so trust files with video extensions.
    const imageTests = files.map((url: string) => {
      return new Promise<string | null>((resolve) => {
        // If video extension, accept immediately
        if (/\.(mp4|webm|mov)$/i.test(url)) return resolve(url);
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => resolve(null);
        img.src = url;
      });
    });

    Promise.all(imageTests).then(results => {
      const found = results.filter(Boolean) as string[];
      const final = found.length ? Array.from(new Set(found)) : [project.image].filter(Boolean);
      setLightboxImages(final);
      setCurrentIndex(0);
      setLightboxOpen(true);
    });
  };

  const closeLightbox = () => setLightboxOpen(false);
  const prev = React.useCallback(() => setCurrentIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length), [lightboxImages.length]);
  const next = React.useCallback(() => setCurrentIndex((i) => (i + 1) % lightboxImages.length), [lightboxImages.length]);

  // Overlay ref to handle outside clicks
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // Keyboard navigation and Esc to close
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, prev, next, lightboxImages.length]);

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] pb-24 transition-colors duration-500">
      {/* Header */}
      <section className="bg-white dark:bg-[#001a35] border-b border-slate-100 dark:border-white/5 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-blue-600 font-semibold uppercase tracking-widest text-sm mb-4 block">Our Work</span>
            <h1 className="text-4xl md:text-5xl font-semibold text-[#4F46E5] dark:text-white mb-6">Innovative Solutions for Modern Businesses</h1>
            <p className="text-slate-500 dark:text-slate-400 text-lg max-w-3xl mx-auto leading-relaxed">
              Explore our diverse portfolio of successful projects where we've helped our partners achieve their digital goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-8 py-3 rounded-2xl font-semibold text-sm transition-all ${
                  activeFilter === filter
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                    : 'bg-white dark:bg-[#001a35] text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 border border-transparent dark:border-white/10'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Project Grid */}
      <section className="max-w-7xl mx-auto px-4">
        <motion.div 
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode='popLayout'>
            {filteredProjects.map((project, i) => (
              <motion.div
                key={project.title}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="group relative bg-white dark:bg-[#001a35] rounded-[2.5rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all border border-slate-100 dark:border-white/10"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-[#4F46E5]/80 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                     <button onClick={() => openLightbox(project)} className="bg-[#4F46E5] text-[#fff] px-6 py-3 rounded-2xl font-semibold flex items-center gap-2 hover:scale-105 transition-transform">
                       View Case Study <ExternalLink size={18} />
                     </button>
                  </div>
                  <div className="absolute top-6 left-6">
                    <div className="bg-white/90 dark:bg-[#4F46E5]/90 backdrop-blur-md px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-[#4F46E5] dark:text-white">
                      {project.category}
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-[#4F46E5] dark:text-white group-hover:text-blue-600 transition-colors">{project.title}</h3>
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">
                      {React.createElement(project.icon, { size: 20 })}
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    {project.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* Collaboration CTA */}
      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) closeLightbox(); }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6">
          <div className="relative max-w-[90%] max-h-[90%] w-full">
            <button aria-label="Close gallery" onClick={closeLightbox} className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-2 rounded-full">
              <X size={20} className="text-white" />
            </button>

            <div className="relative bg-black rounded-xl overflow-hidden">
              {lightboxImages.length ? (
                (/\.(mp4|webm|mov)$/i.test(lightboxImages[currentIndex]) ? (
                  <video controls className="w-full h-[70vh] object-contain bg-black/80">
                    <source src={lightboxImages[currentIndex]} />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img src={lightboxImages[currentIndex]} alt={`Slide ${currentIndex+1}`} className="w-full h-[70vh] object-contain bg-black/80" />
                ))
              ) : (
                <div className="w-full h-[70vh] flex items-center justify-center text-white/80">No images available</div>
              )}

              {lightboxImages.length > 1 && (
                <>
                  <button onClick={prev} aria-label="Previous" className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full">
                    <ChevronLeft className="text-white" />
                  </button>
                  <button onClick={next} aria-label="Next" className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full">
                    <ChevronRight className="text-white" />
                  </button>
                </>
              )}
            </div>
            <div className="mt-3 text-center text-sm text-white/80">{lightboxImages.length ? `${currentIndex+1} / ${lightboxImages.length}` : '0 / 0'}</div>
          </div>
        </div>
      )}
      <section className="mt-24 max-w-7xl mx-auto px-4">
         <div className="bg-[#002a5c] rounded-[3.5rem] p-12 md:p-20 text-center relative overflow-hidden">
            <div className="relative z-10">
               <h2 className="text-3xl md:text-5xl font-semibold text-white mb-6">Have a Great Idea?</h2>
               <p className="text-blue-200 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
                 We're always excited to work on new challenges. Let's build something extraordinary together.
               </p>
               <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                  <Link to="/contact" className="w-full sm:w-auto px-10 py-5 bg-white/10 text-white rounded-2xl font-semibold border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center">
                 
                    Start Your Project
                  </Link>
                  <Link to="/contact" className="w-full sm:w-auto px-10 py-5 bg-white/10 text-white rounded-2xl font-semibold border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center">
                    Request a Consultation
                  </Link>
               </div>
            </div>
            {/* Graphics */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl -mr-48 -mt-48" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl -ml-32 -mb-32" />
         </div>
      </section>
    </div>
  );
};

export default Portfolio;
